/**
 * Sonner-backed notification shim — same API shape as @mantine/notifications.
 * Import from here instead of @mantine/notifications.
 *
 * Supports `id`, `loading`, and `update` so a loading toast can be replaced
 * in place (and given a finite duration) once the work finishes.
 */
import { toast } from 'sonner';

function mantineColorToToast(color) {
  if (!color) return 'default';
  if (color === 'red')    return 'error';
  if (color === 'green')  return 'success';
  if (color === 'yellow' || color === 'orange' || color === 'teal') return 'warning';
  if (color === 'blue' || color === 'cyan' || color === 'violet') return 'info';
  return 'default';
}

// Emit (or update, when `id` matches an existing toast) a Sonner toast.
function emit({ id, title, message, color, autoClose, loading } = {}) {
  const text = message || '';
  const heading = title || text;
  // Only set description when there's a separate title — otherwise the same
  // text would appear twice (once as toast heading, once as description).
  const opts = {
    description: title ? text : undefined,
    // Loading toasts stay until replaced; otherwise honor autoClose.
    duration: loading ? Infinity : (autoClose === false ? Infinity : (typeof autoClose === 'number' ? autoClose : 4000)),
  };
  if (id !== undefined) opts.id = id; // same id → Sonner updates the existing toast in place

  if (loading) return toast.loading(heading, opts);
  const type = mantineColorToToast(color);
  if (type === 'error')   return toast.error(heading, opts);
  if (type === 'success') return toast.success(heading, opts);
  if (type === 'warning') return toast.warning(heading, opts);
  if (type === 'info')    return toast.info(heading, opts);
  return toast(heading, opts);
}

export const notifications = {
  show(o = {})   { return emit(o); },
  update(o = {}) { return emit(o); }, // relies on matching `id` to replace the existing toast
  hide(id)   { toast.dismiss(id); },
  clean()    { toast.dismiss(); },
  cleanQueue() { toast.dismiss(); },
};
